/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import Swiper from 'swiper';
import {commonOptions} from './common.options';
import {bannerCarousel} from '../helpers/banner-carousel.helper';

/**
 * From the document, `navigation`, `pagination` and `scrollbar` can be set to `true` to enable
 * the module with its default options. However, it doesn't work at all.
 *
 * This `defaultOptions` object contains default options for modules above when it is enabled by
 * a simple `boolean` value.
 */
export const defaultOptions = {
  /**
   * @link [Pagination](https://swiperjs.com/swiper-api#pagination)
   */
  navigation: {
    addIcons: false,
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },

  /**
   * @link [Navigation](https://swiperjs.com/swiper-api#navigation)
   */
  pagination: {
    el: '.swiper-pagination',
    type: 'bullets',
    clickable: true,
  },

  /**
   * @link [Scrollbar](https://swiperjs.com/swiper-api#scrollbar)
   */
  scrollbar: {
    el: '.swiper-scrollbar',
    draggable: true,
    snapOnRelease: false,
    hide: false
  },

  /**
   * @link [Autoplay](https://swiperjs.com/swiper-api#autoplay)
   */
  autoplay: {
    delay: 5000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
  },

  /**
   * @link [Free Mode](https://swiperjs.com/swiper-api#free-mode)
   */
  freeMode: {
    enabled: true,
    sticky: true,
    momentum: true,
    momentumRatio: 1,
    momentumVelocityRatio: 1
  },

  /**
   * @link [Mousewheel](https://swiperjs.com/swiper-api#mousewheel)
   */
  mousewheel: {
    forceToAxis: true,
    sensitivity: 1,
    releaseOnEdges: true
  },
};

/**
 * Swiper options container
 *
 * @link [Swiper API](https://swiperjs.com/swiper-api#parameters)
 */

// Define breakpoints for reuse
const responsiveBreakpoints = {
  [commonOptions.breakpoints.xs]: {
    slidesPerView: 1.1,
    spaceBetween: 8
  },

  [commonOptions.breakpoints.sm]: {
    slidesPerView: 2.1,
    spaceBetween: 16
  },

  [commonOptions.breakpoints.md]: {
    slidesPerView: 2.3,
    spaceBetween: 24,
    scrollbar: {
      enabled: false
    }
  },

  [commonOptions.breakpoints.lg]: {
    slidesPerView: 2.4,
    spaceBetween: 24,
    scrollbar: {
      enabled: false
    }
  },

  [commonOptions.breakpoints.xl]: {
    slidesPerView: 3.7,
    spaceBetween: 32,
    scrollbar: {
      enabled: false
    }
  }
};

export const swiperOptions = {
  /**
   * Default options - always applied
   */
  default: {
    navigation: true,
    pagination: false,
    scrollbar: true,

    /**
     * Transition effect. Can be `slide`, `fade`, `cube`, `coverflow`, `flip` or `creative`
     *
     * **Note:** `fade`, `cube`, `coverflow`, `flip` and `creative` only works with `slidesPerView = 1`
     */
    effect: 'slide',

    /**
     * Number of slides per view (slides visible at the same time on slider's container).
     */
    slidesPerView: 4,

    /**
     * Set numbers of slides to define and enable group sliding.
     * Useful to use with `slidesPerView` > 1
     */
    slidesPerGroup: 1,

    /**
     * Distance between slides in pixels.
     */
    spaceBetween: 24,

    /**
     * When true (default), disable loop and prevent swipe when there is only one slide.
     * No slide clone, user cannot swipe.
     */
    noLoopSingleSlide: true,

    on: {
      /**
       * @param {Swiper} swiper
       */
      slidesLengthChange(swiper) {
        // Hide navigation arrows if there is only one slide
        const slidesCount = swiper.el.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
        const { nextEl, prevEl } = swiper.params?.navigation || {};
        const { el: paginationEl } = swiper.params?.pagination || {};

        const hide = slidesCount <= 1;

        const resolveEl = (el) => {
          if (typeof el === 'string') return swiper.el.querySelector(el);
          if (el instanceof HTMLElement) return el;
          return null;
        };

        const toggleClasses = (element, classes) => {
          if (!element) return;
          classes.forEach((cls) => element.classList.toggle(cls, hide));
        };

        const navClasses = ['d-none', 'swiper-button-lock', 'swiper-button-disabled'];
        toggleClasses(resolveEl(nextEl), navClasses);
        toggleClasses(resolveEl(prevEl), navClasses);
        toggleClasses(resolveEl(paginationEl), ['d-none', 'swiper-pagination-lock']);

        swiper.enabled = slidesCount > 1;
      }
    }
  },

  /**
   * Default autoplay configurations
   */
  autoplay: {
    autoplay: {...defaultOptions.autoplay},
  },

  /**
   * Configuration for responsive
   */
  responsive: {
    breakpoints: responsiveBreakpoints,
  },

  /**
   * Car features carousel preset
   *
   * Note: Animation is now handled by the `amb-animate="carousel"` directive.
   * Add the attribute to the `.common--carousel` container element.
   *
   * @example
   *
   * ```html
   * <div class="common--carousel" amb-animate="carousel">
   *   <div amb-swiper="car_features">...</div>
   * </div>
   * ```
   */
  car_features: {
    loop: false,
    pagination: false,
    spaceBetween: 8,
    freeMode: true,
    mousewheel: true,
    breakpoints: {
      ...responsiveBreakpoints
    }
  },

  car_explorer: {
    loop: true,
    scrollbar: false,
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerGroup: 1,
    preventClicks: false,
    preventClicksPropagation: false,
    pagination: {
      el: '.swiper-pagination',
      type: 'fraction'
    },
    on: {
      init(swiper) {
        swiper.enableRotatedTouch = () => enableRotatedTouch(swiper);
        swiper.disableRotatedTouch = () => disableRotatedTouch(swiper);
      },
      destroy(swiper) {
        disableRotatedTouch(swiper);
      }
    }
  },

  /**
   * Vehicle grades carousel preset
   *
   * Used for the Vehicle Grades Carousel component to display grade cards
   * with responsive breakpoints and navigation.
   *
   * @example
   *
   * ```html
   * <div amb-swiper="vehicle_grades">
   *   <div class="swiper-slide">Grade 1</div>
   *   <div class="swiper-slide">Grade 2</div>
   * </div>
   * ```
   */
  vehicle_grades({element} = {}) {
    const gradeCount = element?.querySelectorAll('.swiper-slide.vehicle-grades-carousel__card').length || 0;
    const slidesPerViewByCount = {
      1: {xs: 1, sm: 1, md: 1, lg: 1, xl: 1.24},
      2: {xs: 1.12, sm: 2, md: 2, lg: 2, xl: 3},
      3: {xs: 1.12, sm: 2.2, md: 2.2, lg: 3, xl: 3},
    };
    const slidesPerView = slidesPerViewByCount[gradeCount] || {xs: 1.12, sm: 2.2, md: 2.2, lg: 2.2, xl: 3.5};

    return {
      loop: false,
      pagination: false,
      navigation: {
        nextEl: '.js-carousel-next',
        prevEl: '.js-carousel-prev',
      },
      slidesPerView: slidesPerView.xs,
      spaceBetween: 8,
      freeMode: true,
      mousewheel: true,
      breakpoints: {
        [commonOptions.breakpoints.xs]: {
          slidesPerView: slidesPerView.xs,
          spaceBetween: 8
        },

        [commonOptions.breakpoints.sm]: {
          slidesPerView: slidesPerView.sm,
          spaceBetween: 16
        },

        [commonOptions.breakpoints.md]: {
          slidesPerView: slidesPerView.md,
          spaceBetween: 24,
          scrollbar: {
            enabled: false
          }
        },

        [commonOptions.breakpoints.lg]: {
          slidesPerView: slidesPerView.lg,
          spaceBetween: 24,
          scrollbar: {
            enabled: false
          }
        },

        [commonOptions.breakpoints.xl]: {
          slidesPerView: slidesPerView.xl,
          spaceBetween: 32,
          scrollbar: {
            enabled: false
          }
        }
      }
    };
  },

  /**
   * Promotions carousel preset
   *
   * Used for Promotion and Campaign content types in the Carousel component.
   */
  promotions: {
    loop: false,
    pagination: false,
    freeMode: true,
    mousewheel: true,
    spaceBetween: 8,
    breakpoints: {
      [commonOptions.breakpoints.xs]: {
        slidesPerView: 1.06,
        spaceBetween: 8
      },

      [commonOptions.breakpoints.sm]: {
        slidesPerView: 2.26,
        spaceBetween: 16
      },

      [commonOptions.breakpoints.md]: {
        slidesPerView: 2.3,
        spaceBetween: 24,
        scrollbar: {
          enabled: false
        }
      },

      [commonOptions.breakpoints.lg]: {
        slidesPerView: 3.06,
        spaceBetween: 24,
        scrollbar: {
          enabled: false
        }
      },

      [commonOptions.breakpoints.xl]: {
        slidesPerView: 4.12,
        spaceBetween: 32,
        scrollbar: {
          enabled: false
        }
      }
    }
  },

  compact_promotion: {
    loop: false,
    pagination: false,
    freeMode: true,
    mousewheel: true,
    spaceBetween: 8,
    breakpoints: {
      [commonOptions.breakpoints.xs]: {
        slidesPerView: 1.06,
        spaceBetween: 8
      },

      [commonOptions.breakpoints.sm]: {
        slidesPerView: 1.6,
        spaceBetween: 16
      },

      [commonOptions.breakpoints.md]: {
        slidesPerView: 2.5,
        spaceBetween: 24,
        scrollbar: {
          enabled: false
        }
      },

      [commonOptions.breakpoints.lg]: {
        slidesPerView: 3.4,
        spaceBetween: 24,
        scrollbar: {
          enabled: false
        }
      },

      [commonOptions.breakpoints.xl]: {
        slidesPerView: 4.6,
        spaceBetween: 32,
        scrollbar: {
          enabled: false
        }
      }
    }
  },

  /**
   * Promotions list preset
   *
   * Configures the component to display a static, vertical grid list of products that are eligible for specific promotions or
   * marketing campaigns. It disables standard carousel behavior (like looping, pagination, and touch sliding).
   */
  promotion_list: {
    enabled: false,
    manipulate: false,
  },

  banner: {
    autoplay: {
      enabled: true,
      delay: 5000,
      disableOnInteraction: false,
      pauseOnMouseEnter: false,
      manualAutoPlay: true,
    },
    scrollbar: false,
    navigation: false,
    autoHeight: true,

    // `rewind` not `loop`: loop re-parents slide DOM and reloads video iframes.
    rewind: true,
    slidesPerView: 1,
    spaceBetween: 0,
    effect: 'fade',
    speed: 2000,

    on: bannerCarousel.bindEvents()
  },

  thumbnails: {
    navigation: false,
    scrollbar: false,
    loop: false,
    spaceBetween: 16,
    slidesPerView: 'auto',
    watchSlidesProgress: false,
    centeredSlides: false,

    on: {
      /**
       * @param {Swiper} swiper
       */
      beforeInit(swiper) {
        swiper.el.querySelectorAll('.swiper-slide').forEach(slide => {
          // Inline width: slidesPerView 'auto' reads computed width at init.
          slide.style.width = '100px';

          if (!slide.querySelector('.animation')) {
            const animationEl = document.createElement('div');

            animationEl.classList.add('animation');
            slide.appendChild(animationEl);
          }
        });
      }
    }
  }
};

/**
 * Enable rotated touch mode for a CSS-rotated Swiper.
 *
 * When a Swiper container is rotated 90deg via CSS transform, the browser's touch
 * coordinate system no longer matches Swiper's internal axis. This disables Swiper's
 * built-in touch and adds lightweight handlers that detect vertical swipe gestures
 * and map them to slideNext/slidePrev.
 *
 * Exposed on car_explorer Swiper instances as `swiper.enableRotatedTouch()`.
 *
 * @param {Swiper} swiper Swiper instance
 */
function enableRotatedTouch(swiper) {
  if (swiper._rotatedTouch) return;

  swiper.allowTouchMove = false;

  let startY = 0;
  let isSwiping = false;

  const onTouchStart = (e) => {
    startY = e.touches[0].clientY;
    isSwiping = true;
  };

  const onTouchEnd = (e) => {
    if (!isSwiping) return;
    isSwiping = false;

    const deltaY = e.changedTouches[0].clientY - startY;
    const threshold = 50;

    if (Math.abs(deltaY) > threshold) {
      if (deltaY < 0) {
        swiper.slideNext();
      } else {
        swiper.slidePrev();
      }
    }
  };

  swiper.el.addEventListener('touchstart', onTouchStart, {passive: true});
  swiper.el.addEventListener('touchend', onTouchEnd, {passive: true});

  swiper._rotatedTouch = {onTouchStart, onTouchEnd};
}

/**
 * Disable rotated touch mode and restore Swiper's built-in touch.
 *
 * @param {Swiper} swiper Swiper instance
 */
function disableRotatedTouch(swiper) {
  const state = swiper._rotatedTouch;
  if (!state) return;

  swiper.el.removeEventListener('touchstart', state.onTouchStart);
  swiper.el.removeEventListener('touchend', state.onTouchEnd);
  swiper.allowTouchMove = true;

  delete swiper._rotatedTouch;
}
